import { FC, useState, useEffect, MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import Status from 'src/components/Status';
import { formatCurrency } from 'src/utils/format.utils';
import { Address } from 'src/models/Management.interface';
import { ViewOrderDialogProps } from 'src/models/DialogBox.interface';
import { generateInvoicePDF } from 'src/components/Invoice/InvoicePDF';
import {
  ActionMenuPopover,
  MenuItemsWrapper,
  MenuItemWithIcon,
} from 'src/components/StyledComponents/StyledActionMenu';
import Icon from 'src/assets/icons/Icon';
import ConfirmationDialog from 'src/components/DialogBox/ConfirmationDialog/ConfirmationDialog';
import RejectDialog from 'src/components/DialogBox/RejectDialog/RejectDialog';
import UpdateDeliveryDialog from 'src/components/DialogBox/UpdateDeliveryDialog/UpdateDeliveryDialog';
import {
  orderDialogTitles,
  orderDialogSubtitles,
  routeURL,
  userRoles,
  dataStorageKeywords,
} from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import ExpandableText from 'src/components/ExpandableText/ExpandableText';
import { OrderStatus } from 'src/models/enums/OrderStatus.enum';
import { getEncryptedLocalStorageData } from 'src/utils/dataStorageManagement.utils';

import {
  StyledDialog,
  FixedHeaderWrapper,
  ContentWrapper,
  HeaderSection,
  HeaderLeft,
  HeaderRight,
  TitleTypography,
  CloseIcon,
  ActionMenuButton,
  CustomerDateRow,
  InfoBlock,
  InfoBlockRight,
  LabelTypography,
  ValueTypography,
  MainContentGrid,
  LeftColumn,
  RightColumn,
  DeliveryBox,
  DeliveryRow,
  DeliveryFieldBox,
  DeliveryFieldBoxRight,
  DeliveryNoteContainer,
  ApproveBox,
  RejectionNoteBox,
  RejectionLabelTypography,
  AddressBox,
  AddressLabel,
  ContactRow,
  ContactName,
  AddressText,
  BuildingIcon,
  CheckPaymentsButton,
  WalletIcon,
  CheckPaymentsText,
  ItemsSection,
  ItemsSectionTitle,
  ItemsAndTotalsRow,
  ItemsContainer,
  ItemBox,
  ItemColumnName,
  ItemColumnQuantity,
  ItemColumnRight,
  ItemLabel,
  ItemValue,
  TotalsBox,
  TotalsTitle,
  TotalsRow,
  TotalsLabel,
  TotalsLabelBold,
  TotalsLabelOrange,
  TotalsLabelBlue,
  TotalsValue,
  TotalsValueBold,
  TotalsValueOrange,
  TotalsValueBlue,
  GrandTotalBox,
  PaidAmountBox,
  PaidTopRow,
  PaidTopLabel,
  PaidTopValue,
  PaidDivider,
  PaidBottomRow,
  PaidBottomLabel,
  PaidBottomValue,
  LabelWithIcon,
  TooltipContainer,
  TooltipTitle,
  TooltipRow,
  TooltipLabel,
  TooltipValue,
  TooltipTotalRow,
  TooltipTotalLabel,
  TooltipTotalValue,
  InfoIconStyled,
  InfoIconBlue,
  TitleReferenceTypography,
} from './ViewOrderDialog.styles';
import { Tooltip } from '@mui/material';

const ViewOrderDialog: FC<ViewOrderDialogProps> = ({
  open,
  onClose,
  data,
  onCheckPayments,
  onEditOrder,
  onUpdateDelivery,
  onApprove,
  onReject,
  onOrderPayments,
  onRefresh,
}) => {
  const navigate = useNavigate();
  const { showSnackbar } = StyledSnackbar();
  const [userRole, setUserRole] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [deliveryDialogOpen, setDeliveryDialogOpen] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const menuOpen = Boolean(anchorEl);

  useEffect(() => {
    const fetchUserRole = async () => {
      const role = await getEncryptedLocalStorageData(
        dataStorageKeywords.HIGHEST_ROLE
      );
      setUserRole(role);
    };
    fetchUserRole();
  }, []);

  if (!data) return null;

  // Determine which actions to show based on order status and user role
  const isAdminOrOwner =
    userRole === userRoles.ADMIN || userRole === userRoles.OWNER;
  const showApproveReject =
    data.orderStatus === OrderStatus.PENDING && isAdminOrOwner;

  const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditOrder = () => {
    handleMenuClose();
    if (onEditOrder) {
      onEditOrder();
    } else if (data.id) {
      navigate(routeURL.ORDER_EDIT_REDIRECT(data.id));
    }
  };

  const handleUpdateDelivery = () => {
    handleMenuClose();
    if (onUpdateDelivery) {
      onUpdateDelivery();
    } else {
      setDeliveryDialogOpen(true);
    }
  };

  const handleOrderPayments = () => {
    handleMenuClose();
    if (onOrderPayments) onOrderPayments();
  };

  const handlePrintInvoice = async () => {
    handleMenuClose();
    setIsGeneratingPDF(true);
    try {
      await generateInvoicePDF(data);
    } catch (error: unknown) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleApproveConfirm = async () => {
    try {
      const response = await axiosTokenInstance.patch(`orders/${data.id}`, {
        status: 'Approved',
      });
      showSnackbar(
        response.data.message || 'Order approved successfully',
        'success'
      );
      setApproveDialogOpen(false);
      if (onApprove) {
        onApprove();
      }
      onClose();
    } catch (error: unknown) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleRejectConfirm = async (reason: string) => {
    try {
      const response = await axiosTokenInstance.patch(`orders/${data.id}`, {
        status: 'Rejected',
        notes: reason,
      });
      showSnackbar(
        response.data.message || 'Order rejected successfully',
        'success'
      );
      setRejectDialogOpen(false);
      if (onReject) {
        onReject();
      }
      onClose();
    } catch (error: unknown) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleCloseDeliveryDialog = () => {
    setDeliveryDialogOpen(false);
  };

  const handleDeliverySuccess = () => {
    setDeliveryDialogOpen(false);
    onRefresh && onRefresh();
    onClose();
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date
      .toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
      .toUpperCase();
  };

  const itemsSubtotal = data.items.reduce(
    (sum, item) => sum + item.subTotal,
    0
  );

  const discountAmount = (() => {
    if (!data.discount) return 0;
    if (data.discount.amount !== null && data.discount.amount !== undefined) {
      return parseFloat(data.discount.amount.toString());
    }
    if (
      data.discount.percentage !== null &&
      data.discount.percentage !== undefined
    ) {
      return (
        (itemsSubtotal * parseFloat(data.discount.percentage.toString())) / 100
      );
    }
    return 0;
  })();

  const netTotal = itemsSubtotal - discountAmount;

  const surchargesTotal = (() => {
    if (!data.surcharges || !Array.isArray(data.surcharges)) return 0;

    return data.surcharges.reduce((total, surcharge) => {
      if (surcharge.amount !== null && surcharge.amount !== undefined) {
        return total + parseFloat(surcharge.amount.toString());
      }
      if (surcharge.percentage !== null && surcharge.percentage !== undefined) {
        return (
          total +
          (itemsSubtotal * parseFloat(surcharge.percentage.toString())) / 100
        );
      }
      return total;
    }, 0);
  })();

  const totalPaidAmount =
    (data as { totalPaidAmount?: number }).totalPaidAmount ?? 0;
  const paidToBeAmount = data.grandTotal - totalPaidAmount;

  // Determine if there are any menu items available for the action button
  const hasMenuItems = (() => {
    const status = data.orderStatus;
    if (status === 'New') return true;
    if (status === OrderStatus.PENDING) {
      // Edit is always available for pending, approve/reject only for admin/owner
      return true;
    }
    if (
      status === 'Approved' ||
      status === 'Payment Pending' ||
      status === 'Payment Completed' ||
      status === 'Completed'
    ) {
      return true;
    }
    // Rejected status has no menu items
    return false;
  })();

  const renderAddress = (address: Address, label: string) => (
    <AddressBox>
      <AddressLabel>{label}</AddressLabel>
      <ContactRow>
        <BuildingIcon component="i" className="bx bx-buildings" />
        <ContactName>{address.contactPersonName}</ContactName>
      </ContactRow>
      <AddressText>{address.addressLine1}</AddressText>
      {address.addressLine2 && (
        <AddressText>{address.addressLine2}</AddressText>
      )}
      <AddressText>{address.city}</AddressText>
      <AddressText>{address.postCode}</AddressText>
      <AddressText>{address.country}</AddressText>
    </AddressBox>
  );

  return (
    <StyledDialog open={open} onClose={onClose}>
      {/* Fixed Header Section */}
      <FixedHeaderWrapper>
        <HeaderSection>
          <HeaderLeft>
            <TitleTypography>Order </TitleTypography>
            <TitleReferenceTypography>
              #{data.orderNumber}
            </TitleReferenceTypography>
            <Status status={data.orderStatus} />
          </HeaderLeft>
          <HeaderRight>
            <ActionMenuButton
              id="view-order-action-menu-button"
              aria-controls={menuOpen ? 'view-order-action-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={menuOpen ? 'true' : undefined}
              onClick={hasMenuItems ? handleMenuClick : undefined}
              component="button"
              sx={{
                opacity: hasMenuItems ? 1 : 0.5,
                cursor: hasMenuItems ? 'pointer' : 'not-allowed',
                pointerEvents: hasMenuItems ? 'auto' : 'none',
              }}
            >
              <i className="bx bx-dots-horizontal-rounded"></i>
            </ActionMenuButton>
            <CloseIcon component="i" className="bx bx-x" onClick={onClose} />
          </HeaderRight>
        </HeaderSection>
      </FixedHeaderWrapper>

      {/* Scrollable Content */}
      <ContentWrapper>
        {/* Main Content Grid - Left and Right Columns */}
        <MainContentGrid>
          {/* Left Column */}
          <LeftColumn>
            {/* Customer & Date Row */}
            <CustomerDateRow>
              <InfoBlock>
                <LabelTypography>Customer</LabelTypography>
                <ValueTypography>{data.customerName}</ValueTypography>
              </InfoBlock>
              <InfoBlockRight>
                <LabelTypography>Date</LabelTypography>
                <ValueTypography>{formatDate(data.createdAt)}</ValueTypography>
              </InfoBlockRight>
            </CustomerDateRow>

            {/* Delivery Box */}
            <DeliveryBox>
              <DeliveryRow>
                <DeliveryFieldBox>
                  <LabelTypography>Scheduled Delivery Date</LabelTypography>
                  <ValueTypography>
                    {formatDate(data.scheduledDeliveryDate)}
                  </ValueTypography>
                </DeliveryFieldBox>
                <DeliveryFieldBoxRight>
                  <LabelTypography>Actual Delivery Date</LabelTypography>
                  <ValueTypography>
                    {formatDate(data.actualDeliveryDate)}
                  </ValueTypography>
                </DeliveryFieldBoxRight>
              </DeliveryRow>
              {/* Delivery Note - expandable content that pushes other elements */}
              <DeliveryNoteContainer>
                <LabelTypography>Delivery Note</LabelTypography>
                <ExpandableText
                  text={data.deliveryNote}
                  lines={2}
                  placeholder="N/A"
                />
              </DeliveryNoteContainer>
            </DeliveryBox>

            {/* Approve/Reject Box */}
            {data.orderStatus === 'Rejected' ? (
              <ApproveBox>
                <DeliveryRow>
                  <DeliveryFieldBox>
                    <LabelTypography>Rejected Date</LabelTypography>
                    <ValueTypography>
                      {formatDate(data.approvedDate ?? null)}
                    </ValueTypography>
                  </DeliveryFieldBox>
                  <DeliveryFieldBoxRight>
                    <LabelTypography>Rejected User</LabelTypography>
                    <ValueTypography>
                      {data.approvedBy ? data.approvedBy : 'N/A'}
                    </ValueTypography>
                  </DeliveryFieldBoxRight>
                </DeliveryRow>
                <RejectionNoteBox>
                  <RejectionLabelTypography>
                    Rejection Note
                  </RejectionLabelTypography>
                  <ValueTypography>
                    {data.preparedBy ? data.rejectionNotes : 'N/A'}
                  </ValueTypography>
                </RejectionNoteBox>
              </ApproveBox>
            ) : (
              <ApproveBox>
                <DeliveryRow>
                  <DeliveryFieldBox>
                    <LabelTypography>Approved Date</LabelTypography>
                    <ValueTypography>
                      {formatDate(data.approvedDate ?? null)}
                    </ValueTypography>
                    <LabelTypography>Prepared By</LabelTypography>
                    <ValueTypography>
                      {data.preparedBy ? data.preparedBy : 'N/A'}
                    </ValueTypography>
                  </DeliveryFieldBox>
                  <DeliveryFieldBoxRight>
                    <LabelTypography>Approved User</LabelTypography>
                    <ValueTypography>
                      {data.approvedBy ? data.approvedBy : 'N/A'}
                    </ValueTypography>
                  </DeliveryFieldBoxRight>
                </DeliveryRow>
              </ApproveBox>
            )}

            {/* Check Payments Button */}
            {onCheckPayments && (
              <CheckPaymentsButton onClick={onCheckPayments}>
                <WalletIcon component="i" className="bx bx-dollar-circle" />
                <CheckPaymentsText>Check Payments</CheckPaymentsText>
              </CheckPaymentsButton>
            )}
          </LeftColumn>

          {/* Right Column - Addresses */}
          <RightColumn>
            {renderAddress(data.billingAddress, 'Billing Address')}
            {renderAddress(data.deliveryAddress, 'Delivery Address')}
          </RightColumn>
        </MainContentGrid>

        {/* Items Section */}
        <ItemsSection>
          <ItemsSectionTitle>Items</ItemsSectionTitle>
          <ItemsAndTotalsRow>
            <ItemsContainer>
              {/* Header Row */}
              <ItemBox>
                <ItemColumnName>
                  <ItemLabel>Item</ItemLabel>
                </ItemColumnName>
                <ItemColumnQuantity>
                  <ItemLabel>Quantity</ItemLabel>
                </ItemColumnQuantity>
                <ItemColumnQuantity>
                  <ItemLabel>Unit Price</ItemLabel>
                </ItemColumnQuantity>
                <ItemColumnRight>
                  <ItemLabel>Subtotal</ItemLabel>
                </ItemColumnRight>
              </ItemBox>
              {/* Item Rows */}
              {data.items.map((item, index) => (
                <ItemBox key={index}>
                  <ItemColumnName>
                    <ItemValue>{item.name}</ItemValue>
                  </ItemColumnName>
                  <ItemColumnQuantity>
                    <ItemValue>
                      {item.quantity.toLocaleString('en-US')}
                    </ItemValue>
                  </ItemColumnQuantity>
                  <ItemColumnQuantity>
                    <ItemValue>{formatCurrency(item.unitPrice)}</ItemValue>
                  </ItemColumnQuantity>
                  <ItemColumnRight>
                    <ItemValue>{formatCurrency(item.subTotal)}</ItemValue>
                  </ItemColumnRight>
                </ItemBox>
              ))}
            </ItemsContainer>

            {/* Totals Box */}
            <TotalsBox>
              <TotalsTitle>Final Totals</TotalsTitle>
              <TotalsRow>
                <TotalsLabel>Items Subtotal</TotalsLabel>
                <TotalsValue>{formatCurrency(itemsSubtotal)}</TotalsValue>
              </TotalsRow>
              <TotalsRow>
                <LabelWithIcon>
                  <TotalsLabelOrange>Discounts</TotalsLabelOrange>
                  {data.discount && (
                    <Tooltip
                      title={
                        <TooltipContainer>
                          <TooltipTitle>Discount Details</TooltipTitle>
                          <TooltipRow>
                            <TooltipLabel>
                              {data.discount.name || 'Discount'}
                              {data.discount.percentage != null &&
                                ` (${data.discount.percentage}%)`}
                            </TooltipLabel>
                            <TooltipValue>
                              {formatCurrency(discountAmount)}
                            </TooltipValue>
                          </TooltipRow>
                        </TooltipContainer>
                      }
                      arrow
                      placement="top"
                    >
                      <InfoIconStyled>
                        <i className="bx bx-info-circle" />
                      </InfoIconStyled>
                    </Tooltip>
                  )}
                </LabelWithIcon>
                <TotalsValueOrange>
                  {discountAmount > 0
                    ? `- ${formatCurrency(discountAmount)}`
                    : formatCurrency(0)}
                </TotalsValueOrange>
              </TotalsRow>
              <TotalsRow>
                <TotalsLabel>Net Total</TotalsLabel>
                <TotalsValue>{formatCurrency(netTotal)}</TotalsValue>
              </TotalsRow>
              <TotalsRow>
                <LabelWithIcon>
                  <TotalsLabelBlue>Surcharges</TotalsLabelBlue>
                  {Array.isArray(data.surcharges) &&
                    data.surcharges.length > 0 && (
                      <Tooltip
                        title={
                          <TooltipContainer>
                            <TooltipTitle>Surcharges Breakdown</TooltipTitle>
                            <TooltipRow
                              style={{ fontWeight: 600, marginBottom: '4px' }}
                            >
                              <TooltipLabel style={{ fontWeight: 600 }}>
                                Name
                              </TooltipLabel>
                              <TooltipValue style={{ fontWeight: 600 }}>
                                Amount
                              </TooltipValue>
                            </TooltipRow>
                            {(
                              data.surcharges as Array<{
                                name: string;
                                amount: number | null;
                                percentage: number | null;
                              }>
                            ).map((surcharge, index) => {
                              const surchargeAmount =
                                surcharge.amount !== null &&
                                surcharge.amount !== undefined
                                  ? parseFloat(surcharge.amount.toString())
                                  : surcharge.percentage !== null &&
                                    surcharge.percentage !== undefined
                                  ? (itemsSubtotal *
                                      parseFloat(
                                        surcharge.percentage.toString()
                                      )) /
                                    100
                                  : 0;
                              return (
                                <TooltipRow key={index}>
                                  <TooltipLabel>
                                    {surcharge.name}
                                    {surcharge.percentage != null &&
                                      ` (${surcharge.percentage}%)`}
                                  </TooltipLabel>
                                  <TooltipValue>
                                    {formatCurrency(surchargeAmount)}
                                  </TooltipValue>
                                </TooltipRow>
                              );
                            })}
                            <TooltipTotalRow>
                              <TooltipTotalLabel>
                                Total Amount
                              </TooltipTotalLabel>
                              <TooltipTotalValue>
                                {formatCurrency(surchargesTotal)}
                              </TooltipTotalValue>
                            </TooltipTotalRow>
                          </TooltipContainer>
                        }
                        arrow
                        placement="top"
                      >
                        <InfoIconBlue>
                          <i className="bx bx-info-circle" />
                        </InfoIconBlue>
                      </Tooltip>
                    )}
                </LabelWithIcon>
                <TotalsValueBlue>
                  {surchargesTotal > 0
                    ? `+ ${formatCurrency(surchargesTotal)}`
                    : formatCurrency(0)}
                </TotalsValueBlue>
              </TotalsRow>
              <GrandTotalBox>
                <TotalsLabelBold>Grand Total</TotalsLabelBold>
                <TotalsValueBold>
                  {formatCurrency(data.grandTotal)}
                </TotalsValueBold>
              </GrandTotalBox>
              <PaidAmountBox>
                <PaidTopRow>
                  <PaidTopLabel>Total Paid Amount</PaidTopLabel>
                  <PaidTopValue>{formatCurrency(totalPaidAmount)}</PaidTopValue>
                </PaidTopRow>
                <PaidDivider />
                <PaidBottomRow>
                  <PaidBottomLabel>Paid to be Amount</PaidBottomLabel>
                  <PaidBottomValue>
                    {formatCurrency(paidToBeAmount)}
                  </PaidBottomValue>
                </PaidBottomRow>
              </PaidAmountBox>
            </TotalsBox>
          </ItemsAndTotalsRow>
        </ItemsSection>
      </ContentWrapper>

      {/* Action Menu Popover */}
      <ActionMenuPopover
        width="fit-content"
        id="view-order-action-menu"
        open={menuOpen}
        anchorEl={anchorEl}
        onClose={handleMenuClose}
      >
        <MenuItemsWrapper>
          {data.orderStatus === 'New' && (
            <MenuItemWithIcon
              onClick={handleEditOrder}
              iconClassName="bx bx-edit"
              text="Edit Order"
            />
          )}

          {data.orderStatus === OrderStatus.PENDING && (
            <>
              <MenuItemWithIcon
                onClick={handleEditOrder}
                iconClassName="bx bx-edit"
                text="Edit Order"
              />
              {showApproveReject && (
                <>
                  <MenuItemWithIcon
                    onClick={() => {
                      handleMenuClose();
                      setApproveDialogOpen(true);
                    }}
                    iconClassName="bx bx-check"
                    text="Approve"
                  />
                  <MenuItemWithIcon
                    onClick={() => {
                      handleMenuClose();
                      setRejectDialogOpen(true);
                    }}
                    iconClassName="bx bx-x"
                    text="Reject"
                  />
                </>
              )}
            </>
          )}

          {data.orderStatus === 'Approved' && (
            <>
              <MenuItemWithIcon
                onClick={handleUpdateDelivery}
                icon={
                  <Icon
                    name="InvoiceDollar"
                    size={16}
                    color="var(--primary-navy)"
                  />
                }
                text="Update Delivery Details"
              />
              <MenuItemWithIcon
                onClick={handleOrderPayments}
                iconClassName="bx bx-dollar-circle"
                text="Order Payments"
              />
            </>
          )}

          {data.orderStatus === 'Payment Pending' && (
            <>
              <MenuItemWithIcon
                onClick={handleUpdateDelivery}
                icon={
                  <Icon
                    name="InvoiceDollar"
                    size={16}
                    color="var(--primary-navy)"
                  />
                }
                text="Update Delivery Details"
              />
              <MenuItemWithIcon
                onClick={handleOrderPayments}
                iconClassName="bx bx-dollar-circle"
                text="Order Payments"
              />
            </>
          )}

          {data.orderStatus === 'Payment Completed' && (
            <>
              <MenuItemWithIcon
                onClick={handleUpdateDelivery}
                icon={
                  <Icon
                    name="InvoiceDollar"
                    size={16}
                    color="var(--primary-navy)"
                  />
                }
                text="Update Delivery Details"
              />
              <MenuItemWithIcon
                onClick={handleOrderPayments}
                iconClassName="bx bx-dollar-circle"
                text="Order Payments"
              />
            </>
          )}

          {data.orderStatus === 'Completed' && (
            <MenuItemWithIcon
              onClick={handleOrderPayments}
              iconClassName="bx bx-dollar-circle"
              text="Order Payments"
            />
          )}

          {/* Print Invoice - Available for all statuses */}
          <MenuItemWithIcon
            onClick={handlePrintInvoice}
            iconClassName="bx bx-printer"
            text={isGeneratingPDF ? 'Generating...' : 'Print Invoice'}
          />
        </MenuItemsWrapper>
      </ActionMenuPopover>

      {/* Dialogs */}
      <UpdateDeliveryDialog
        open={deliveryDialogOpen}
        onClose={handleCloseDeliveryDialog}
        rowId={data.id}
        data={data}
        onSuccess={handleDeliverySuccess}
      />

      <ConfirmationDialog
        height="275px"
        open={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
        onConfirm={handleApproveConfirm}
        title={orderDialogTitles.APPROVE}
        subtitle={orderDialogSubtitles.APPROVE}
        confirmText="Approve"
        confirmButtonColor="var(--success)"
        alertIconColor="var(--success)"
      />

      <RejectDialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onConfirm={handleRejectConfirm}
        title="Reject Order"
        subtitle="Rejection Note"
        subtitleSecondary=""
        placeholder="Reason for rejection"
        confirmText="Reject"
      />
    </StyledDialog>
  );
};

export default ViewOrderDialog;
